// import { getToApi } from 'venuescanner-js/services/api'
import isSSR from "venuescanner-js/services/isSSR";
import Plan from "./plan";
import Tiers from "venuescanner-js/constants/tiers";
import PlanTags from "venuescanner-js/constants/subscriptions";
// import serviceURLs from '../serviceURLs.json'
import controllers from "../controllers";
/** @module
 * @name planService
 * @desc Service class for fetching Subscription Plans
 */
const Plans = {
  versions: {
    standard: {
      // url: serviceURLs.plans,
      plans: null,
      inflights: [],
    },
  },
  inflights: [],
  /** @function
   * @name fetch
   * @param {string} [version=standard] - Defaults to standard
   * @desc Fetches all Subscription plans in current system version
   * @returns {Promise<Plan[]>}
   */
  fetch: async function (version = "standard") {
    if (!this.versions[version])
      throw new Error(`${version} is not an accepted venue version`);
    if (this.versions[version].plans) return this.versions[version].plans;
    return new Promise((resolve) => {
      this.versions[version].inflights.push(resolve);
      if (this.versions[version].inflights.length === 1) this.load(version);
    });
  },
  /** @function
   * @name load
   * @param {String} [version=standard]  - Defaults to standard
   * @desc Used by the class to perform the actual api call for the Plans and then resolves all the inflights with instances of the Plans
   * @returns {Promise<Plan[]>}
   */
  load: function (version = "standard") {
    if (isSSR() || window.isTest) {
      this.versions[version].plans = [];
      if (!isSSR() && window.isTest) {
        while (this.versions[version].inflights.length) {
          const resolve = this.versions[version].inflights.shift();
          resolve(this.versions[version].plans);
        }
      }
    } else {
      // getToApi(this.versions[version].url, { returnData: true }
      controllers.planService.plans.get().then(({ data: plans }) => {
        this.versions[version].plans = plans.map((v) => new Plan(v));
        while (this.versions[version].inflights.length) {
          const resolve = this.versions[version].inflights.shift();
          resolve(this.versions[version].plans);
        }
      });
    }
  },
  /** @function
   * @name byId
   * @param {String} id  - The MongoID of the Plan to find
   * @param {String} [version=standard]  - Defaults to standard
   * @desc Ensures the Plans are loaded, then finds the Plan with the given ID
   * @returns {Promise<Plan>}
   */
  byId: async function (id, version = "standard") {
    const plans = await this.fetch(version);
    return plans.find((plan) => plan._id === id);
  },
  /** @function
   * @name filter
   * @param {Object} options  - The Tier to filter by
   * @param {String} [options.tier="tier4"]  - Filter by the Tier
   * @param {Boolean} [options.monthly=true]  - Return Monthly Plans, otherwise Annual
   * @param {String} [options.above]  - The Plan tag to which the plans must be above
   * @param {Integer} [options.trialPeriod]  - The max Trial period in days
   * @param {String} [options.version="standard"] - Defines the data schema (only one defined)
   * @desc Ensures the Plans are loaded, then finds the Plans with the given Tier
   * @returns {Promise<Plan[]>}
   */
  filter: async function ({
    tier = Tiers.tier4,
    monthly = true,
    trialPeriod = null,
    above = null,
    version = "standard",
  }) {
    if (!Tiers[tier]) return [];
    const plans = (await this.fetch(version))
      .filter((plan) => plan.tier === tier)
      .filter((plan) => plan.isMonthly(monthly));
    const tags = Object.values(PlanTags);
    if (above) {
      // console.log({ tags, above })
      if (tags.includes(above)) {
        const idx = tags.indexOf(above);
        tags.splice(0, idx + 1);
      }
    }
    if (!trialPeriod) {
      return plans
        .filter((plan) => tags.includes(plan.tag))
        .filter((plan) => !plan.trial || !plan.trial.length);
    }
    const byTag = plans
      .filter((plan) => tags.includes(plan.tag))
      .reduce((acc, plan) => {
        if (!acc[plan.tag]) acc[plan.tag] = [];
        if (plan.trial && plan.trial.length > trialPeriod) return acc;
        acc[plan.tag].push(plan);
        // console.log({ trialLength: (plan.trial ? (plan.trial.length || 0) : 0) })
        acc[plan.tag] = acc[plan.tag].sort((a, b) => {
          return (
            (b.trial ? b.trial.length || 0 : 0) -
            (a.trial ? a.trial.length || 0 : 0)
          );
        });
        return acc;
      }, {});
    return Object.values(PlanTags)
      .map((tag) => {
        if (byTag[tag] && byTag[tag].length) return byTag[tag].shift();
        return null;
      })
      .filter((p) => p);
  },
};
if (typeof window !== "undefined") window.Plans = Plans;
export default Plans;
